import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import { FaEnvelope } from "react-icons/fa";
import Fqicon from "../../assets/img/Fq.png";
import UserService from "../../services/UserService";
import AlertService from "../../services/AlertService";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const emailRef = useRef(null);

  const [emailError, setEmailError] = useState("");
  const [captchaValid, setCaptchaValid] = useState(false);
  const [loading, setLoading] = useState(false);

  /* ================= CAPTCHA VALIDATION ================= */
  const onCaptchaChange = async (token) => {
    if (!token) return;

    setLoading(true);
    try {
      const data = await UserService.ValidateReCaptcha(token);
      const isValid = data?.isSuccess ?? data?.data ?? false;
      setCaptchaValid(isValid);

      if (!isValid) {
        AlertService.error("Captcha verification failed");
      }
    } catch (err) {
      console.error(err);
      setCaptchaValid(false);
      AlertService.error("Captcha verification failed");
    } finally {
      setLoading(false);
    }
  };

  /* ================= FORM SUBMIT ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();
    const email = emailRef.current.value;

    if (!email.trim()) {
      setEmailError("Email is required");
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError("Enter a valid email address");
      return;
    }

    if (!captchaValid) {
      AlertService.error("Please verify you are not a robot");
      return;
    }

    try {
      setLoading(true);
      const result = await UserService.ForgotPassword(email);

      if (result.isSuccess) {
        AlertService.success(
          "A link to reset the password is sent to your email successfully!"
        );
        navigate("/login");
      } else {
        AlertService.error(result.errorMessage);
      }
    } catch (err) {
      console.error(err);
      AlertService.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex font-sans">

      {/* ================= LEFT PANEL ================= */}
      <div className="hidden md:flex w-1/2 bg-[#0e3a46] relative overflow-hidden">
        <div className="absolute top-12 -right-10 bg-white h-24 w-56 rounded-full flex items-center pl-12 shadow-md">
          <img src={Fqicon} alt="Logo" className="h-12" />
        </div>
      </div>

      {/* ================= RIGHT PANEL ================= */}
      <div className="flex w-full md:w-1/2 items-center justify-center bg-white px-4">
        <form
          onSubmit={handleSubmit}
          className="w-full max-w-[420px]"
        >
          <h2 className="text-3xl font-semibold mb-2">
            Forgot Password
          </h2>

          <p className="text-base text-gray-600 mb-6 leading-relaxed font-medium">
            Don’t worry — we’ll reset your password and send you a link
            to create a new one.
          </p>

          {/* ================= EMAIL INPUT ================= */}
          <div>
            <div
              className={`
                flex items-center
                border rounded-md px-3 h-12
                focus-within:ring-2 focus-within:ring-[#0e3a46]
                ${emailError ? "border-red-500" : "border-gray-300"}
              `}
            >
              <FaEnvelope className="text-gray-400 mr-3" />
              <input
                ref={emailRef}
                type="email"
                placeholder="Email"
                className="flex-1 text-base outline-none bg-transparent"
                onChange={() => setEmailError("")}
              />
            </div>

            {emailError && (
              <p className="text-red-500 text-sm mt-1">
                {emailError}
              </p>
            )}
          </div>

          {/* ================= CAPTCHA ================= */}
          <div className="mt-6 mb-6 scale-[0.9] origin-left">
            <ReCAPTCHA
              sitekey={process.env.REACT_APP_SITEKEY}
              onChange={onCaptchaChange}
            />
          </div>

          {/* ================= BUTTONS ================= */}
          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => navigate("/login")}
              disabled={loading}
              className="
                flex-1 h-11
                border border-gray-400
                rounded-md
                text-gray-700
                hover:bg-gray-50
                focus:ring-2 focus:ring-[#0e3a46]
              "
            >
              Back to Login
            </button>

            <button
              type="submit"
              disabled={loading || !captchaValid}
              className={`
                flex-1 h-11
                rounded-md
                text-white
                bg-[#0e3a46]
                hover:opacity-95
                focus:ring-2 focus:ring-[#0e3a46]
                ${(!captchaValid || loading) && "opacity-50 cursor-not-allowed"}
              `}
            >
              {loading ? "Sending..." : "Send reset link"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ForgotPassword;